(() => {
  if (window.NaskiMotion) return;
  window.NaskiMotion = { version: '1.0.0' };

  const body = document.body;
  if (!body) return;
  body.classList.add('nsk-body');

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const page = location.pathname.replace(/^\/|\/$/g, '') || 'home';
  const pageClass = page
    .replace(/^pages\//, '')
    .replace(/^the-/, '')
    .replace(/[^a-z0-9]+/g, '-');
  body.classList.add(`nsk-page-${pageClass}`);
  const themeMain = document.querySelector('#MainContent');
  const template = themeMain?.dataset.template?.split(/[.\/]/)[0];
  if (template) body.classList.add(`nsk-template-${template.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`);
  const clearGateState = () => {
    try { sessionStorage.removeItem('nsk-gate-arrive'); } catch (_e) {}
    body.classList.remove('nsk-gate-leaving', 'nsk-gate-arrived', 'nsk-motion-paused');
  };
  clearGateState();
  addEventListener('pageshow', clearGateState);

  /* ---- reveals ---------------------------------------------------------------------------
     The old list named .tenet/.pcard/.step/.seal/.tile/.fit/.ncard/.nworn__cell/
     .product-grid__card/.resource-card. None of those exist on the catalogue or the product page
     any more: both matched ZERO elements, so 182 catalogue cards and the entire PDP scrolled in
     flat while the machinery sat there working perfectly on nothing. The list below names what
     the pages are actually built from, and [data-rv] lets any section opt in from Liquid without
     another edit to this file.

     Deliberately still an IntersectionObserver rather than animation-timeline:view(). The native
     timeline is driven by scroll POSITION, so a reveal done that way plays backwards when you
     scroll up. One-shot is the behaviour this brand already has, and it is worth more than the
     observer costs. */
  const REVEAL_SELECTOR = [
    '[data-rv]',
    '.tenet', '.pcard', '.step', '.seal', '.tile', '.fit', '.ncard', '.nworn__cell',
    '.product-grid__card', '.resource-card',
    '.band__pt',                             /* PDP copy bands */
    '.rel__row',                             /* the related-pieces rail, revealed as ONE unit */
    '.art',                                  /* Sacred Art blocks, 29 of them, the whole page */
    '.scarwrap'                              /* the Sacred Art marquee, again as ONE unit */

    /* Three things deliberately NOT in this list, each learned the hard way on the draft:

       .npc  — the catalogue card already has its own reveal in naskico-cardcss:
               `animation:cardin .5s forwards` on a --i stagger, with its own reduced-motion
               override. A CSS animation with forwards outranks this transition, so adding .npc
               here did nothing except make the cards look un-held. The catalogue was never
               missing a reveal; I was checking for the wrong system.

       .acc__b — the body of a <details>. A closed accordion never intersects, so gating it left
               9 of 19 PDP blocks stuck at opacity 0.

       .rel__c — an individual card inside a HORIZONTAL rail. Cards scrolled off to the right
               never intersect vertically, so they stayed invisible until the rail was scrolled.
               The rail container reveals instead.

       .scar__c — same shape of mistake and worse: it sits in .scar__track, an infinite 62s
               marquee. Its container .scarwrap is the target instead. */
  ].join(',');

  const revealLive = !reduced.matches && ('IntersectionObserver' in window);
  const revealObserver = revealLive ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('nsk-in');
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: .12, rootMargin: '0px 0px -4% 0px' }) : null;

  /* Stagger within each container. With one counter across the whole document a 74-card grid
     inherited whatever index the header happened to stop on, so the first row arrived mid-cycle. */
  const revealSeen = new WeakMap();
  /* Membership, not an attribute. An attribute marker is copied by cloneNode along with everything
     else, so a clone looked observed when it never was, which is precisely the case this guards. */
  const revealSeenByObserver = new WeakSet();

  function scanReveals(root) {
    const scope = root || document;
    scope.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
      if (element.hasAttribute('data-nsk-reveal') || element.classList.contains('nsk-in')) return;
      const parent = element.parentElement || document.body;
      const n = revealSeen.get(parent) || 0;
      revealSeen.set(parent, n + 1);
      element.style.setProperty('--nsk-index', String(n % 6));
      if (element.classList.contains('rv')) return;
      /* Never gate anything inside a closed disclosure: it cannot intersect, so it would simply
         stay invisible. */
      if (element.closest('details:not([open])')) return;
      element.setAttribute('data-nsk-reveal', '');
      element.classList.add('nsk-in');
    });
  }

  scanReveals();
  window.NSKMotion = window.NSKMotion || {};
  window.NSKMotion.scan = scanReveals;

  /* Failsafe. Two .nworn__cell tiles on the homepage were sitting at opacity 0 permanently: they
     carry the attribute, so they were scanned, but they were never observed, which is what happens
     when a grid clones an already-scanned node (a clone copies attributes and nothing else).
     Anything still gated after this window gets shown. A missed animation is a nuance; an
     invisible product card is a defect. */
  if (revealLive) setTimeout(function () {
    /* A single timed sweep is not enough: it only sees what is near the viewport at the moment it
       runs, so a stray tile far down the page stayed invisible. This follows the scroll instead,
       showing anything still gated once it comes within a viewport and a half. The observer
       remains the mechanism; this only catches nodes it never got to observe. */
    /* Only elements the observer never took. Anything carrying data-nsk-obs is being watched and
       will reveal on its own, so it is left alone and keeps its animation. What is left is nodes
       that hold the attribute without ever having been observed, which is what a clone of an
       already-scanned element looks like: two .nworn__cell tiles on the homepage sat at opacity 0
       permanently that way. Position is not used, because those tiles were far enough down the page
       that a viewport-relative sweep never reached them. */
    document.querySelectorAll('[data-nsk-reveal]:not(.nsk-in)').forEach(function (el) {
      if (!revealSeenByObserver.has(el)) el.classList.add('nsk-in');
    });
    document.addEventListener('nsk:content', function () { scanReveals(); }, { passive: true });
  }, 5000);

  /* The sweep above runs once, and the two homepage tiles turned out to be inserted AFTER it: a
     grid clones a scanned node later in the page's life, so the clone carries the reveal attribute,
     was never observed, and no timer was ever going to catch it. Watch for them arriving and hand
     them to the observer, which keeps their animation rather than forcing them visible. */
  if (revealLive && 'MutationObserver' in window) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          var list = n.matches && n.matches('[data-nsk-reveal]') ? [n] : [];
          if (n.querySelectorAll) list = list.concat([].slice.call(n.querySelectorAll('[data-nsk-reveal]')));
          for (var k = 0; k < list.length; k++) {
            var el = list[k];
            if (revealSeenByObserver.has(el) || el.classList.contains('nsk-in')) continue;
            revealSeenByObserver.add(el);
            revealObserver.observe(el);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Hook for any container that builds its children client-side after this file runs: mark it
     [data-nsk-live] and anything matching the selector above is picked up when it lands. The
     catalogue does not need this, since its cards carry their own animation. */
  const liveGrid = document.querySelector('[data-nsk-live]');
  if (liveGrid && 'MutationObserver' in window) {
    let queued = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(queued);
      queued = requestAnimationFrame(() => scanReveals(liveGrid));
    }).observe(liveGrid, { childList: true, subtree: true });
  }

/* data-nsk-parallax disabled to save GPU layers */

  document.querySelectorAll('.seal').forEach((seal) => {
    if (!seal.hasAttribute('tabindex')) seal.tabIndex = 0;
  });

  const creedVerse = document.querySelector('.nsk-page-creed .verse');
  if (creedVerse && 'IntersectionObserver' in window) {
    const creedObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        creedVerse.classList.add('nsk-in');
        creedObserver.disconnect();
      }
    }, { threshold: .45 });
    creedObserver.observe(creedVerse);
  }

  const ambient = [...document.querySelectorAll('.ticker__t,.ntick__t,.nstrip__t,.matte__glow')];
  if ('IntersectionObserver' in window) {
    const ambientObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.style.animationPlayState = entry.isIntersecting && !document.hidden ? 'running' : 'paused';
      });
    }, { threshold: 0 });
    ambient.forEach((element) => ambientObserver.observe(element));
  }

  const videos = [...document.querySelectorAll('video[autoplay]')];
  const videoVisibility = new Map();
  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        videoVisibility.set(entry.target, entry.isIntersecting);
        if (reduced.matches || !entry.isIntersecting) entry.target.pause();
        else entry.target.play().catch(() => {});
      });
    }, { threshold: .08 });
    videos.forEach((video) => videoObserver.observe(video));
  }

  document.addEventListener('visibilitychange', () => {
    body.classList.toggle('nsk-motion-paused', document.hidden);
    ambient.forEach((element) => {
      element.style.animationPlayState = document.hidden ? 'paused' : '';
    });
    videos.forEach((video) => {
      if (document.hidden || reduced.matches || videoVisibility.get(video) === false) video.pause();
      else video.play().catch(() => {});
    });
  });

  const motionHero = document.querySelector('.nvhero');
  const heroVideo = motionHero?.querySelector('video');
  if (motionHero && heroVideo && !reduced.matches && 'requestVideoFrameCallback' in HTMLVideoElement.prototype) {
    let complete = false;
    let started = false;
    const setBeat = (time) => {
      if (complete) return;
      const beat = time < .5 ? 0 : time < 1.1 ? 1 : time < 2.0 ? 2 : time < 3.2 ? 3 : 4;
      motionHero.dataset.nskBeat = String(beat);
      if (beat === 4) {
        complete = true;
        motionHero.classList.add('nsk-sync-complete');
      }
    };
    const frame = (_now, metadata) => {
      if (!started) {
        started = true;
        motionHero.classList.add('nsk-sync-ready');
      }
      setBeat(metadata.mediaTime);
      if (!complete) heroVideo.requestVideoFrameCallback(frame);
    };
    motionHero.dataset.nskBeat = '0';
    heroVideo.requestVideoFrameCallback(frame);
    setTimeout(() => {
      if (!started) motionHero.classList.add('nsk-sync-complete');
    }, 1800);
  }

  const relicHero = document.querySelector('.nhero');
  if (relicHero && 'IntersectionObserver' in window) {
    const relicObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        relicHero.classList.add('nsk-relic-in');
        relicObserver.disconnect();
      }
    }, { threshold: .28 });
    relicObserver.observe(relicHero);
  }

  document.querySelectorAll('.art.has-fits').forEach((tile) => {
    const control = tile.querySelector('.art__fit');
    const images = [...tile.querySelectorAll('.art__stack .art__img')];
    if (!control || images.length < 2) return;
    control.setAttribute('role', 'button');
    control.setAttribute('tabindex', '0');
    control.setAttribute('aria-label', 'Cycle artwork and worn views');
    let index = Math.max(0, images.findIndex((image) => image.classList.contains('on')));
    const show = (next) => {
      index = (next + images.length) % images.length;
      images.forEach((image, imageIndex) => image.classList.toggle('on', imageIndex === index));
      tile.classList.add('nsk-touch-active');
      control.textContent = index === 0 ? '✝ See It Worn' : `✝ View ${index + 1} of ${images.length}`;
    };
    const activate = (event) => {
      event.preventDefault();
      event.stopPropagation();
      show(index + 1);
    };
    control.addEventListener('click', activate);
    control.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') activate(event);
    });
    if (!finePointer.matches) control.textContent = '✝ See It Worn';
  });

  const fitTiles = [...document.querySelectorAll('.fit,.look,.nsk-page-lookbook .frame,.nsk-page-lookbook .feat')];
  if (fitTiles.length && 'HTMLDialogElement' in window) {
    const dialog = document.createElement('dialog');
    dialog.className = 'nsk-fit-dialog';
    dialog.setAttribute('aria-label', 'Expanded NaskiCo fit');
    dialog.innerHTML = '<button class="nsk-fit-dialog__close" type="button" aria-label="Close expanded fit">×</button><div class="nsk-fit-dialog__grid"><div class="nsk-fit-dialog__media"></div><div class="nsk-fit-dialog__copy"><div class="nsk-fit-dialog__eyebrow">✝ Worn Not Whispered</div><div class="nsk-fit-dialog__title"></div><div class="nsk-fit-dialog__verse"></div></div></div>';
    body.appendChild(dialog);
    const dialogMedia = dialog.querySelector('.nsk-fit-dialog__media');
    let dialogImage;
    const dialogTitle = dialog.querySelector('.nsk-fit-dialog__title');
    const dialogVerse = dialog.querySelector('.nsk-fit-dialog__verse');
    const close = () => dialog.close();
    dialog.querySelector('.nsk-fit-dialog__close').addEventListener('click', close);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    fitTiles.forEach((tile) => {
      if (!tile.hasAttribute('tabindex')) tile.tabIndex = 0;
      tile.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          tile.click();
        }
      });
      tile.addEventListener('click', (event) => {
        if (/^\/products\//.test(tile.getAttribute('href') || '')) return;
        if (event.target.closest('button,input,select,textarea')) return;
        const nestedLink = event.target.closest('a[href]');
        if (nestedLink && nestedLink !== tile) return;
        const image = tile.querySelector('img');
        if (!image) return;
        event.preventDefault();
        const title = tile.querySelector('.t')?.textContent?.trim() || image.alt.replace(/\s+(worn|\u2014).*$/i, '').trim();
        const verse = tile.querySelector('.v')?.textContent?.trim() || '';
        if (!dialogImage) {
          dialogImage = document.createElement('img');
          dialogMedia.appendChild(dialogImage);
        }
        dialogImage.src = image.currentSrc || image.src;
        dialogImage.alt = image.alt;
        dialogTitle.textContent = title;
        dialogVerse.textContent = verse;
        const open = () => dialog.showModal();
        open();
      });
    });
  }

  /* Redline fix 2026-10-24: removed nsk-gate-leaving click hijack that drew #A40E1F line at top:50% */

  body.classList.add('nsk-motion-ready');
})();


/* ============ NSK INTERACTION PACK JS — Tier 1+2 (2026-07-12) ============ */
(function () {
  var body = document.body;
  if (!body || !body.classList.contains('nsk-body')) return;
  body.classList.add('nsk-t1');
  var fine = matchMedia('(hover:hover) and (pointer:fine)').matches;

  /* T1.1: immediately mark lazy images nsk-ld so no image is ever held at opacity:0 */
  document.querySelectorAll('main img[loading="lazy"]').forEach(function (im) {
    im.classList.add('nsk-ld');
  });

  /* T1.5 nav: tighten on scroll + active link */
  var lastS = false;
  addEventListener('scroll', function () {
    var s = scrollY > 80;
    if (s !== lastS) { lastS = s; body.classList.toggle('nsk-scrolled', s); }
  }, { passive: true });
  var path = location.pathname.replace(/\/$/, '');
  document.querySelectorAll('.nav__links a, .nnav__links a').forEach(function (a) {
    var href = (a.getAttribute('href') || '').replace(/\/$/, '');
    if (href && href === path) a.classList.add('nsk-active');
  });

  /* T1.3 card tilt + T1.4 magnetic CTAs disabled to prevent mousemove getBoundingClientRect() reflows on click */

  /* T2 reveal hook for seals + rungs */
  var toIn = document.querySelectorAll('.seal,.nseal,.rung');
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('nsk-in'); io.unobserve(e.target); } });
    }, { threshold: 0.25 });
    toIn.forEach(function (el) { io.observe(el); });
  } else toIn.forEach(function (el) { el.classList.add('nsk-in'); });

  /* T2 Sacred Art lightbox */
  var arts = document.querySelectorAll('a.art');
  if (arts.length) {
    var lb = document.createElement('div');
    lb.className = 'nsk-lb';
    lb.innerHTML = '<div class="nsk-lb__back"></div><figure class="nsk-lb__f"><img alt="" decoding="async" src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"><figcaption><b></b><span></span><a class="nsk-lb__cta" href="/collections/all">Shop The Catalogue</a></figcaption></figure><button class="nsk-lb__x" aria-label="Close">×</button>';
    body.appendChild(lb);
    var lim = lb.querySelector('img'), ltt = lb.querySelector('b'), lvv = lb.querySelector('figcaption span');
    function lbClose() { lb.classList.remove('on'); document.documentElement.style.overflow = ''; }
    lb.querySelector('.nsk-lb__back').addEventListener('click', lbClose);
    lb.querySelector('.nsk-lb__x').addEventListener('click', lbClose);
    addEventListener('keydown', function (e) { if (e.key === 'Escape') lbClose(); });
    arts.forEach(function (a) {
      a.addEventListener('click', function (e) {
        /* A tile that points at a real product must NAVIGATE. This lightbox was written when every
           Sacred Art tile pointed at the waitlist, so swallowing the click cost nothing. Now each
           tile links to its own PDP, and intercepting here made those links unreachable: the hrefs
           were correct and no click ever arrived at one. The lightbox still serves gallery tiles
           that have no product behind them. */
        if (/^\/products\//.test(a.getAttribute('href') || '')) return;
        var img = a.querySelector('img');
        if (!img) return;
        e.preventDefault();
        lim.src = (img.currentSrc || img.src).replace(/_\d+x(\d+)?\./, '.');
        lim.alt = img.getAttribute('alt') || (a.querySelector('.art__cap .t') || {}).textContent || 'Sacred Art';
        ltt.textContent = (a.querySelector('.art__cap .t') || {}).textContent || '';
        lvv.textContent = (a.querySelector('.art__cap .v') || {}).textContent || '';
        lb.classList.add('on');
        document.documentElement.style.overflow = 'hidden';
      });
    });
  }

  /* T2 watch form theatre (seal + sparks). NSK V2 2026-10-23: no longer intercepts the submit.
     It used to preventDefault and call wf.submit() 560ms later; a programmatic submit skips Shopify's
     hCaptcha step, which is what produced "Missing CAPTCHA token" (HTTP 400). Now the sparks are
     purely decorative and the native submit (with Shopify's captcha) carries on untouched. */
  var wf = document.getElementById('watchform');
  if (wf) {
    wf.addEventListener('submit', function () {
      if (wf.dataset.sealing) return;
      var btn = wf.querySelector('button');
      if (!btn) return;
      setTimeout(function () {
        if (!wf.dataset.nskSending) return;
        wf.dataset.sealing = '1';
        btn.classList.add('nsk-sealing');
        for (var i = 0; i < 10; i++) {
          var sp = document.createElement('i');
          sp.className = 'nsk-spark';
          sp.style.setProperty('--a', (i * 36) + 'deg');
          sp.style.setProperty('--d', (0.45 + Math.random() * 0.5).toFixed(2));
          btn.appendChild(sp);
        }
      }, 0);
    });
  }

  /* T2 testimony caption sync (Mali, Worthy) */
  var tv = document.getElementById('ntestvid'), tq = document.querySelector('.ntest__q');
  if (tv && tq) {
    var tem = tq.querySelector('em');
    tv.addEventListener('timeupdate', function () {
      var t = tv.currentTime;
      tq.classList.toggle('nsk-say', t >= 10.2 && t < 13.2);
      if (tem) tem.classList.toggle('nsk-say2', t >= 13.2 && t < 17);
    });
  }
})();

/* ============ NSK CINEMA PACK JS — grain · eyebrow decode · ticker velocity (2026-08-10) ============ */
(function () {
  var body = document.body;
  if (!body || !body.classList.contains('nsk-body')) return;
  var reduce = matchMedia('(prefers-reduced-motion:reduce)').matches;

  /* 1) film grain + vignette (once, global) */
/* .nsk-cine grain overlay disabled to prevent full-screen blend buffer */

  /* 2) hero eyebrow decode — left-to-right glyph resolve */
  if (!reduce) {
    var GL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/·+';
    document.querySelectorAll('.nvhero__eb, .nhero__eb').forEach(function (el) {
      if (el.dataset.nskDecoded) return;
      el.dataset.nskDecoded = '1';
      var full = el.textContent;
      var chars = full.split('');
      el.classList.add('nsk-decoding');
      var DUR = 620, start = 0;
      var tick = function (now) {
        if (!start) start = now;
        var t = Math.min(1, (now - start) / DUR);
        var resolved = Math.floor(t * chars.length);
        el.textContent = chars.map(function (c, i) {
          if (c === ' ' || c === '\u00a0' || c === '\u2727' || c === '\u2726' || c === '\u00b7') return c;
          if (i < resolved) return c;
          return GL[(Math.random() * GL.length) | 0];
        }).join('');
        if (t < 1) requestAnimationFrame(tick);
        else { el.textContent = full; el.classList.remove('nsk-decoding'); }
      };
      requestAnimationFrame(tick);
    });
  }

  /* 3) ticker velocity lean */
  if (!reduce) {
    var tickers = [].slice.call(document.querySelectorAll('.ntick, .nstrip'));
    if (tickers.length) {
      var lastY = scrollY, vel = 0, raf = null;
      var apply = function () {
        raf = null;
        var sk = Math.max(-5, Math.min(5, vel * 0.05));
        for (var i = 0; i < tickers.length; i++) tickers[i].style.setProperty('--nsk-skew', sk.toFixed(2) + 'deg');
        vel *= 0.85;
        if (Math.abs(vel) > 0.5) raf = requestAnimationFrame(apply);
        else for (var j = 0; j < tickers.length; j++) tickers[j].style.setProperty('--nsk-skew', '0deg');
      };
      addEventListener('scroll', function () {
        vel = scrollY - lastY; lastY = scrollY;
        if (!raf) raf = requestAnimationFrame(apply);
      }, { passive: true });
    }
  }
})();

/* ============ NSK PREMIUM PACK v2 JS (2026-08-10) ============ */
(function () {
  var body = document.body;
  if (!body || !body.classList.contains('nsk-body')) return;
  var reduce = matchMedia('(prefers-reduced-motion:reduce)').matches;
  var fine = matchMedia('(hover:hover) and (pointer:fine)').matches;

  /* 2) scroll progress hairline (site-wide) */
  var prog = document.createElement('div'); prog.className = 'nsk-prog'; body.appendChild(prog);
  var setProg = function () { var h = document.documentElement; var m = h.scrollHeight - h.clientHeight; prog.style.transform = 'scaleX(' + (m > 0 ? (scrollY / m) : 0).toFixed(4) + ')'; };
  addEventListener('scroll', setProg, { passive: true }); setProg();

  /* 5) back-to-top pill */
  var toTop = document.createElement('button'); toTop.className = 'nsk-top'; toTop.type = 'button';
  toTop.setAttribute('aria-label', 'Back to top'); toTop.textContent = '\u2191'; body.appendChild(toTop);
  toTop.addEventListener('click', function () { scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }); });
  addEventListener('scroll', function () { toTop.classList.toggle('on', scrollY > innerHeight * 1.4); }, { passive: true });

  /* 1) custom cursor ring disabled to keep main thread 100% idle on mousemove/hover */

  /* 6) PDP main-image hover-zoom */
  if (fine) {
    document.querySelectorAll('.npdp__stage img').forEach(function (im) {
      var wrap = im.closest('.product-gallery__media,.npdp__stage,.npdp__main,.product-media') || im.parentElement;
      if (!wrap || wrap.dataset.nskZoom) return; wrap.dataset.nskZoom = '1'; wrap.classList.add('nsk-zoom');
      wrap.addEventListener('mousemove', function (e) { var r = wrap.getBoundingClientRect();
        im.style.transformOrigin = ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
        im.style.transform = 'scale(1.55)'; });
      wrap.addEventListener('mouseleave', function () { im.style.transform = ''; });
    });
  }

  /* 7) hero parallax depth */
  if (!reduce) {
    var hm = document.querySelector('.nvhero__vid, .nvhero video');
    if (hm) addEventListener('scroll', function () { var y = Math.min(scrollY, 900); hm.style.transform = 'translate3d(0,' + (y * 0.12).toFixed(1) + 'px,0)'; }, { passive: true });
  }

  /* 10) smooth in-page anchor scroll */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return; var id = a.getAttribute('href'); if (!id || id === '#') return;
    var tgt = document.querySelector(id); if (!tgt) return;
    e.preventDefault(); tgt.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  });
})();

/* ============ NSK SHOPPABLE LOOKBOOK HOTSPOTS JS (2026-08-10) ============ */
(function () {
  var body = document.body; if (!body || !body.classList.contains('nsk-body')) return;
  var fine = matchMedia('(hover:hover) and (pointer:fine)').matches;
  document.querySelectorAll('a.fit').forEach(function (fit) {
    if (fit.dataset.nskShop) return; fit.dataset.nskShop = '1';
    var t = (fit.querySelector('.fit__cap .t') || {}).textContent || '';
    var v = (fit.querySelector('.fit__cap .v') || {}).textContent || '';
    if (!t) return;
    var hot = document.createElement('span'); hot.className = 'fit__hot'; hot.setAttribute('aria-hidden', 'true'); hot.textContent = '\u271D';
    fit.appendChild(hot);
    if (fine) {
      var panel = document.createElement('div'); panel.className = 'fit__shop'; panel.setAttribute('aria-hidden', 'true');
      panel.innerHTML = '<b></b><i></i><u>\u271D Get This Drop \u00b7 Join The Watch \u2192</u>';
      panel.querySelector('b').textContent = t;
      panel.querySelector('i').textContent = (v ? v + ' \u00b7 ' : '') + 'Printed to order';
      fit.appendChild(panel);
    }
  });
})();


/* ============ NSK MEMORY HYGIENE & INSTANT CACHE PREFETCHER (2026-10-25) ============ */
(function () {
  /* 1. Cap localStorage/sessionStorage caches so stale data never bloats browser storage */
  try {
    var rec = JSON.parse(localStorage.getItem('nsk_recent') || '[]');
    if (Array.isArray(rec) && rec.length > 6) {
      localStorage.setItem('nsk_recent', JSON.stringify(rec.slice(0, 6)));
    }
    var sav = JSON.parse(localStorage.getItem('nskSavedV1') || '[]');
    if (Array.isArray(sav) && sav.length > 24) {
      localStorage.setItem('nskSavedV1', JSON.stringify(sav.slice(0, 24)));
    }
  } catch (_e) {}

  /* 2. Memory-safe single-slot HTTP cache prefetcher on hover/touchstart */
  var con = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (con && (con.saveData || con.effectiveType === 'slow-2g' || con.effectiveType === '2g')) return;
  var prefetchedUrl = '';
  var hoverTimer = null;
  function warmUrl(href) {
    if (!href || href === prefetchedUrl || href === location.pathname + location.search) return;
    if (!/^\/(products|collections|pages)\//.test(href)) return;
    prefetchedUrl = href;
    var link = document.getElementById('nsk-instant-prefetch');
    if (!link) {
      link = document.createElement('link');
      link.id = 'nsk-instant-prefetch';
      link.rel = 'prefetch';
      link.as = 'document';
      document.head.appendChild(link);
    }
    link.href = href;
  }
  document.addEventListener('pointerover', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    var h = a.getAttribute('href') || '';
    if (!/^\/(products|collections|pages)\//.test(h)) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { warmUrl(h); }, 65);
  }, { passive: true });
  document.addEventListener('touchstart', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    var h = a.getAttribute('href') || '';
    if (/^\/(products|collections|pages)\//.test(h)) warmUrl(h);
  }, { passive: true });
})();
